import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { APP_CONFIG } from '@application/config';
import { TSpaceActivities, TActivitySpace, TBookingSettings, TUnavailableSpace, EActivityBase } from '../entities/booking.model';
import { Observable } from 'rxjs';
import { TActivities } from '../entities/settings.model';

@Injectable({
  providedIn: 'root'
})
export class BookingService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(APP_CONFIG).apiUrl;

  /**
   * Gets all activities associated with spaces
   * @returns Observable with the list of space activities
   */
  public getAllSpaceActivities(): Observable<TSpaceActivities[]> {
    return this.http.get<TSpaceActivities[]>(`${this.apiUrl.booking_service}/SpaceActivities/GetAll`, { withCredentials: true });
  }

  /**
   * Gets activities associated with a specific space
   * @param spaceId ID of the space
   * @returns Observable with the space activities
   */
  public getSpaceActivitiesBySpaceId(spaceId: string): Observable<TSpaceActivities> {
    return this.http.get<TSpaceActivities>(
      `${this.apiUrl.booking_service}/SpaceActivities?id=${spaceId}`,
      { withCredentials: true }
    );
  }

  /**
   * Adds a new activity to a space
   * @param data Activity data to be added
   * @returns Observable with the added activity data
   */
  public addSpaceActivity(data: Partial<TSpaceActivities>): Observable<TSpaceActivities> {
    return this.http.post<TSpaceActivities>(
      `${this.apiUrl.booking_service}/SpaceActivities`,
      data,
      { withCredentials: true }
    );
  }

  /**
   * Updates an existing space activity
   * @param data Updated activity data
   * @returns Observable with the updated data
   */
  public updateSpaceActivity(data: Partial<TSpaceActivities>): Observable<TSpaceActivities> {
    return this.http.put<TSpaceActivities>(
      `${this.apiUrl.booking_service}/SpaceActivities`,
      data,
      { withCredentials: true }
    );
  }

  /**
   * Removes a space activity
   * @param spaceActivityId ID of the space activity to be removed
   * @returns Observable indicating completion
   */
  public removeSpaceActivity(spaceActivityId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl.booking_service}/SpaceActivities?id=${spaceActivityId}`,
      { withCredentials: true }
    );
  }

  /**
   * Removes a space activity
   * @param spaceActivitiesId ID of the space activity to be removed
   * @returns Observable indicating completion
   */
  public removeActivities(spaceActivitiesId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl.booking_service}/Activities?id=${spaceActivitiesId}`,
      { withCredentials: true }
    );
  }

  /**
   * Detaches a specific activity from a space
   * @param activitySpaceId ID of the relationship between activity and space
   * @returns Observable indicating completion
   */
  public detachActivityFromSpace(activitySpaceId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl.booking_service}/SpaceActivities/DetachActivity?id=${activitySpaceId}`,
      { withCredentials: true }
    );
  }

  /**
   * Attaches an existing activity to a space
   * @param spaceId ID of the space
   * @param activityId ID of the activity
   * @returns Observable with the attachment data
   */
  public attachActivityToSpace(spaceId: string, activityId: string): Observable<TActivitySpace> {
    return this.http.post<TActivitySpace>(
      `${this.apiUrl.booking_service}/SpaceActivities/AttachActivity`,
      { spaceId, activityId },
      { withCredentials: true }
    );
  }

  /**
   * Updates the space of an activity
   * @param activityId ID of the activity
   * @param newSpaceId ID of the new space
   * @returns Observable with the updated data
   */
  public moveActivityToSpace(activityId: string, newSpaceId: string): Observable<TActivitySpace> {
    return this.http.patch<TActivitySpace>(
      `${this.apiUrl.booking_service}/SpaceActivities/MoveActivity`,
      { activityId, newSpaceId },
      { withCredentials: true }
    );
  }

  public getBookingSettings(): Observable<TBookingSettings> {
    return this.http.get<TBookingSettings>(`${this.apiUrl.booking_service}/BookingSettings`, { withCredentials: true });
  }

  public updateBookingSettings(payload: Partial<TBookingSettings>): Observable<TBookingSettings> {
    return this.http.patch<TBookingSettings>(`${this.apiUrl.booking_service}/BookingSettings`,
      payload,
      { withCredentials: true });
  }

  public getUnavailableSpaces(): Observable<TUnavailableSpace[]> {
    return this.http.get<TUnavailableSpace[]>(`${this.apiUrl.booking_service}/UnavailableSpace/GetAll`, { withCredentials: true });
  }

  public addUnavailableSpace(payload: Partial<TUnavailableSpace>): Observable<TUnavailableSpace> {
    return this.http.post<TUnavailableSpace>(
      `${this.apiUrl.booking_service}/UnavailableSpace`,
      payload,
      { withCredentials: true }
    );
  }

  public updateUnavailableSpace(payload: Partial<TUnavailableSpace>): Observable<TUnavailableSpace> {
    return this.http.put<TUnavailableSpace>(
      `${this.apiUrl.booking_service}/UnavailableSpace`,
      payload,
      { withCredentials: true }
    );
  }

  public removeUnavailableSpace(unavailableSpaceId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl.booking_service}/UnavailableSpace?id=${unavailableSpaceId}`,
      { withCredentials: true }
    );
  }

  public getAllActivities(): Observable<{ "@odata.context": string; value: TActivities[] }> {
    return this.http.get<{ "@odata.context": string; value: TActivities[] }>(`${this.apiUrl.activity_service}/Odata/Activities`, {
      params: {
        activityBase: EActivityBase.All,
      },
      withCredentials: true,
    });
  }

  public validateUnavailableSpace(unavailableSpace: Partial<TUnavailableSpace>): Observable<boolean> {
    return this.http.post<boolean>(`${this.apiUrl.booking_service}/BookingException/ValidateUnavailableSpace`, unavailableSpace, {
      withCredentials: true,
    });
  }


  public cancelBookingsByUnavailableSpace(unavailableSpace: Partial<TUnavailableSpace>): Observable<void> {
    return this.http.post<void>(`${this.apiUrl.booking_service}/BookingException/CancelBookingsByUnavailableSpace`, unavailableSpace, {
      withCredentials: true,
    });
  }
}
